import { Draft, PayloadAction } from '@reduxjs/toolkit';
import {
  IPredicateFieldsFragment,
  Maybe,
  Order,
} from '../../../../utils/apollo/graphqlSchema';
import { IBuilder } from '../types';
import { defaultBreakdownGroup } from './defaults';
import { IBuilderBreakdownGroup } from './types';
import { IBinning } from '../binning/types';

export const addBreakdownGroupReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    groupLimit: number;
  }>,
) => {
  const { groupLimit } = action.payload;

  const groups = draft.breakdown.groups;

  if (groups.length < groupLimit) {
    groups.push(defaultBreakdownGroup);
  } else {
    throw new Error('Group limit reached');
  }
};

export const removeBreakdownGroupReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    groupIndex: number;
  }>,
) => {
  const { groupIndex } = action.payload;

  const groups = draft.breakdown.groups;

  if (!groups.length) {
    throw new Error('No groups to reset');
  }

  draft.breakdown.groups.splice(groupIndex, 1);
};

export const resetBreakdownGroupsReducer = (draft: Draft<IBuilder>) => {
  if (!draft.breakdown.groups.length) {
    throw new Error('No groups to reset');
  }

  draft.breakdown.groups = [];
};

export const changeBreakdownOrderReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<Order>,
) => {
  draft.breakdown.order = action.payload;
};

export const toggleBreakdownOverallReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<boolean>,
) => {
  draft.breakdown.overall = action.payload;
};

export const setBreakdownGroupFieldReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    groupIndex: number;
    field: Maybe<IBuilderBreakdownGroup['field']>;
  }>,
) => {
  const { groupIndex, field } = action.payload;

  if (!draft.breakdown.groups[groupIndex]) {
    throw new Error('Group does not exist');
  }

  // Field change will reset breakdown group to default
  draft.breakdown.groups[groupIndex] = {
    ...defaultBreakdownGroup,
    field,
  };
};

export const setBreakdownGroupFilterReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    groupIndex: number;
    filter: Maybe<IPredicateFieldsFragment>;
  }>,
) => {
  const { groupIndex, filter } = action.payload;

  if (!draft.breakdown.groups[groupIndex]) {
    throw new Error('Group does not exist');
  }

  draft.breakdown.groups[groupIndex].filter = filter;
};

export const setBreakdownGroupBinningReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    groupIndex: number;
    newBinning: Maybe<IBinning>;
  }>,
) => {
  const { groupIndex, newBinning } = action.payload;

  const group = draft.breakdown.groups?.[groupIndex];

  if (!group) {
    throw new Error('Group does not exist');
  }

  group.binning = newBinning;
};
